<template>
  <div class="dashboard-filter-card surface-card p-4 shadow-2 border-round-xl mb-4">
    <div class="filter-header mb-3 flex align-items-center justify-content-between">
      <h4 class="m-0 text-900 font-semibold flex align-items-center gap-2">
        <i class="pi pi-filter text-primary text-xl"></i>
        Filtros Generales del Dashboard
      </h4>
      <Button
        label="Limpiar Filtros"
        icon="pi pi-filter-slash"
        class="p-button-text p-button-secondary p-button-sm"
        @click="resetearFiltros"
      />
    </div>

    <!-- Grid de Filtros -->
    <div class="p-fluid grid formgrid">
      <!-- 1. Fecha Inicio -->
      <div class="field col-12 sm:col-6 lg:col-2">
        <label for="fecha_inicio" class="font-medium text-700 text-sm mb-2 block">
          <i class="pi pi-calendar mr-1 text-primary"></i> Fecha Inicio
        </label>
        <Calendar
          id="fecha_inicio"
          v-model="filtros.fecha_inicio"
          dateFormat="dd/mm/yy"
          showIcon
          class="w-full"
          :maxDate="filtros.fecha_fin"
        />
      </div>

      <!-- 2. Fecha Fin -->
      <div class="field col-12 sm:col-6 lg:col-2">
        <label for="fecha_fin" class="font-medium text-700 text-sm mb-2 block">
          <i class="pi pi-calendar mr-1 text-primary"></i> Fecha Fin
        </label>
        <Calendar
          id="fecha_fin"
          v-model="filtros.fecha_fin"
          dateFormat="dd/mm/yy"
          showIcon
          class="w-full"
          :minDate="filtros.fecha_inicio"
        />
      </div>

      <!-- 3. Selector de Sucursal -->
      <div class="field col-12 sm:col-6 lg:col-2">
        <label for="sucursal" class="font-medium text-700 text-sm mb-2 block">
          <i class="pi pi-building mr-1 text-primary"></i> Sucursal
        </label>
        <Dropdown
          id="sucursal"
          v-model="filtros.sucursal_id"
          :options="opcionesSucursales"
          optionLabel="nombre"
          optionValue="id"
          placeholder="Todas"
          showClear
          :loading="cargandoAuxiliares"
          class="w-full"
        />
      </div>

      <!-- 4. Selector de Grupos/Categorías -->
      <div class="field col-12 sm:col-6 lg:col-3">
        <label for="grupos" class="font-medium text-700 text-sm mb-2 block">
          <i class="pi pi-tags mr-1 text-primary"></i> Grupos
        </label>
        <MultiSelect
          id="grupos"
          v-model="filtros.grupo_ids"
          :options="opcionesGrupos"
          optionLabel="nombre"
          optionValue="id"
          placeholder="Seleccionar"
          :display="'chip'"
          :loading="cargandoAuxiliares"
          class="w-full"
        />
      </div>

      <!-- 5. Canal de Venta (Opcional) -->
      <div class="field col-12 sm:col-6 lg:col-1" v-if="mostrarFiltroCanal">
        <label class="font-medium text-700 text-sm mb-2 block">
          <i class="pi pi-wallet mr-1 text-primary"></i> Canal
        </label>
        <SelectCanalVenta 
          v-model="filtros.canal_venta_id" 
          :mostrarTodos="true"
          class="w-full"
        />
      </div>

      <!-- Botón de Aplicar Filtros -->
      <div class="field col-12 lg:col-2 flex align-items-end">
        <Button
          label="Aplicar Filtros"
          icon="pi pi-search"
          class="p-button-primary w-full sm:w-auto px-4"
          :loading="loading"
          @click="aplicarFiltros"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import SucursalService from '@/service/SucursalService';
import GrupoService from '@/service/GrupoService';
import SelectCanalVenta from '@/components/shared/SelectCanalVenta.vue';

/**
 * @component DashboardFilter
 * @description Componente de filtrado dinámico para tableros analíticos.
 * Encapsula la lógica de selección de rango de fechas, sucursal activa
 * y categorización de productos.
 *
 * @emits aplicar-filtros Emite el objeto estandarizado con todos los filtros activos.
 */
export default {
  name: 'DashboardFilter',
  components: {
    Calendar,
    Dropdown,
    MultiSelect,
    Button,
    SelectCanalVenta
  },

  props:{
    loading:{
        type: Boolean,
        default: false
    },
    sucursalInicialId:{
        type: [Number,String],
        default: null
    },
    mostrarFiltroCanal: {
        type: Boolean,
        default: false
    }
  },

  emits:['aplicar-filtros'],

  data() {
    return {
        cargandoAuxiliares:false,
        opcionesSucursales:[],
        opcionesGrupos:[],
        // Estado reactivo interno de los filtros
        filtros:{
            fecha_inicio: null,
            fecha_fin: null,
            sucursal_id: null,
            canal_venta_id: null,
            grupo_ids: [6, 10], // 6 = Pizza Jr, 10 = Pizza Familiar
        }  
    };
  },
  created(){
    this.sucursalService = new SucursalService();
    this.grupoService = new GrupoService();
    // Preparamos las fechas por defecto (1er día del mes a la fecha actual)
    this.inicializarFechasDefecto();
  },

  mounted() {
    // 1. Cargamos las opciones de los combos (Sucursales y Grupos)
    this.cargarDatosAuxiliares();
    
    // 2. Disparamos el evento para avisar al Dashboard.vue de los filtros iniciales
    this.aplicarFiltros();
  },

  methods: {
    /**
     * Establecer lsa fechas por defecto: 
     * Primer dia del mes actual hasta la fecha presente
     */
    inicializarFechasDefecto(){
        const hoy = new Date();

        this.filtros.fecha_inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        this.filtros.fecha_fin = hoy;
        this.filtros.sucursal_id = this.sucursalInicialId;
    },

    /**
     * Carga los listados para los combos (Sucursales y Grupos de Productos).
     */
    async cargarDatosAuxiliares(){
        this.cargandoAuxiliares = true;
        try{
            const [resSucursales, resGrupos] = await Promise.allSettled([
                this.sucursalService.getSucursalesAll(),
                this.grupoService.getGrupoAll()
            ]);
            if (resSucursales.status === 'fulfilled') {
                this.opcionesSucursales = resSucursales.value.data || resSucursales.value || [];
            }
            if (resGrupos.status === 'fulfilled') {
                let datosCrudos = resGrupos.value.data || resGrupos.value || {};
                let todosLosGrupos = [];
                
                // Buscamos dónde está realmente el array en la respuesta
                if (Array.isArray(datosCrudos)) {
                    todosLosGrupos = datosCrudos;
                } 
                // AQUÍ: Como mostraste en el JSON, los datos vienen dentro de la propiedad "grupos"
                else if (datosCrudos.grupos && Array.isArray(datosCrudos.grupos)) {
                    todosLosGrupos = datosCrudos.grupos;
                } 
                // Por si acaso viene en una propiedad genérica "data"
                else if (datosCrudos.data && Array.isArray(datosCrudos.data)) {
                    todosLosGrupos = datosCrudos.data;
                } 
                // Último recurso (objetos asociativos puros)
                else {
                    todosLosGrupos = Object.values(datosCrudos);
                }

                // Ahora filtramos los grupos que tienen estado = 1 (Activos)
                this.opcionesGrupos = todosLosGrupos.filter(grupo => grupo.estado === 1);
            }
        }catch(error){
            console.error('Error al cargar datos auxiliares del filtro:', error);
        } finally {
          this.cargandoAuxiliares = false;
        }
    },
    /**
     * Normaliza las fechas a cadenas 'YYYY-MM-DD HH:mm:ss' y emite el evento al padre.
     */
    aplicarFiltros() {
      if (!this.filtros.fecha_inicio || !this.filtros.fecha_fin) {
        return;
      }

      // Copia limpia con fechas formateadas para backend
      const payload = {
        fecha_inicio: this.formatearFechaBD(this.filtros.fecha_inicio, '00:00:00'),
        fecha_fin: this.formatearFechaBD(this.filtros.fecha_fin, '23:59:59'),
        sucursal_id: this.filtros.sucursal_id || null,
        canal_venta_id: this.filtros.canal_venta_id || null,
        grupo_ids: this.filtros.grupo_ids && this.filtros.grupo_ids.length > 0 
          ? this.filtros.grupo_ids 
          : null,
        // Mantener las instancias Date originales por si un widget las requiere
        _rawFechaInicio: this.filtros.fecha_inicio,
        _rawFechaFin: this.filtros.fecha_fin
      };

      this.$emit('aplicar-filtros', payload);
    },
    /**
     * Restablece los filtros a su estado inicial y emite la actualización.
     */
    resetearFiltros() {
      this.inicializarFechasDefecto();
      this.filtros.grupo_ids = [6, 10]; // 6 = Pizza Jr, 10 = Pizza Familiar
      this.filtros.canal_venta_id = null;
      this.aplicarFiltros();
    },

    /**
     * Helper para dar formato ISO estándar a objetos Date de JS.
     */
    formatearFechaBD(fecha, hora) {
      if (!(fecha instanceof Date)) return fecha;
      const yyyy = fecha.getFullYear();
      const mm = String(fecha.getMonth() + 1).padStart(2, '0');
      const dd = String(fecha.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hora}`;
    }
  },
};
</script>
<style scoped>
.dashboard-filter-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
}
.filter-header {
  border-bottom: 1px solid #f1f3f5;
  padding-bottom: 0.75rem;
}
</style>